import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useI18n } from "../lib/i18n";
import { apiClient, formatApiError } from "../lib/api";
import { Send, Loader2, CheckCircle2 } from "lucide-react";
import Wordmark from "./Wordmark";
import Vision2030Badge from "./Vision2030Badge";
import { FEATURES } from "../lib/features";

export default function Footer() {
  const { t, lang } = useI18n();
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState(null);

  const submitQuick = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      await apiClient.post("/contact-messages", {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        subject: lang === "ar" ? "رسالة سريعة من التذييل" : "Quick footer message",
        message: form.message.trim(),
      });
      setDone(true);
      setForm({ name: "", email: "", message: "" });
      setTimeout(() => setDone(false), 4000);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <footer className="mt-24 border-t border-[#0a0a0a]/[0.08] bg-white" data-testid="site-footer">
      {/* ── Contact section (above footer columns) ─────────────────── */}
      <div className="border-b border-[#0a0a0a]/[0.06]" id="contact-section" data-testid="footer-contact-section">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 py-14">
          <div className="grid lg:grid-cols-[1fr_1.2fr] gap-10 items-start">
            <div>
              <div className="overline overline-gold mb-3">{lang === "ar" ? "تواصل معنا" : "GET IN TOUCH"}</div>
              <h2 className="font-display text-3xl sm:text-4xl tracking-tight text-[#0a0a0a] leading-tight">
                {lang === "ar" ? "نسعد بالاستماع إليك" : "We'd love to hear from you"}
              </h2>
              <p className="text-sm sm:text-base text-[#525252] mt-3 max-w-md leading-relaxed">
                {lang === "ar"
                  ? "اقتراحات، شكاوى، أو فرص شراكة — أرسل رسالة سريعة وسنرد على بريدك خلال 24 ساعة."
                  : "Suggestions, complaints, or partnership ideas — send a quick message and we'll reply within 24 hours."}
              </p>
              <ul className="mt-6 space-y-2.5 text-sm">
                <li>
                  <Link to="/contact" className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 font-bold text-sm text-[#0a0a0a] shadow-md hover:shadow-lg transition-all hover:scale-[1.02]" style={{ background: "linear-gradient(135deg,#d4b56e,#c9a55c,#8a6f2e)" }} data-testid="footer-contact-link">
                    {lang === "ar" ? "تواصل معنا" : "Contact us"}
                    <span aria-hidden>→</span>
                  </Link>
                </li>
              </ul>
            </div>

            <form
              onSubmit={submitQuick}
              className="rounded-3xl border-2 border-[#0a0a0a]/8 p-6 sm:p-7 bg-[#fafaf7]"
              data-testid="footer-contact-form"
            >
              {done ? (
                <div className="py-8 text-center" data-testid="footer-contact-success">
                  <CheckCircle2 className="w-10 h-10 text-green-600 mx-auto mb-3" />
                  <p className="font-semibold text-[#0a0a0a]">{lang === "ar" ? "تم استلام رسالتك" : "Message received"}</p>
                  <p className="text-xs text-[#6b6b6b] mt-1">
                    {lang === "ar" ? "سنرد على بريدك خلال 24 ساعة." : "We'll reply within 24 hours."}
                  </p>
                </div>
              ) : (
                <>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-[#6b6b6b]">{lang === "ar" ? "الاسم" : "Name"}</label>
                      <input
                        type="text"
                        className="input-dark mt-1"
                        required
                        minLength={2}
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        data-testid="footer-contact-name"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-[#6b6b6b]">{lang === "ar" ? "البريد الإلكتروني" : "Email"}</label>
                      <input
                        type="email"
                        className="input-dark mt-1"
                        required
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        data-testid="footer-contact-email"
                      />
                    </div>
                  </div>
                  <div className="mt-3">
                    <label className="text-xs text-[#6b6b6b]">{lang === "ar" ? "رسالتك" : "Your message"}</label>
                    <textarea
                      className="input-dark mt-1"
                      rows={3}
                      required
                      minLength={10}
                      placeholder={lang === "ar" ? "كيف يمكننا مساعدتك؟" : "How can we help?"}
                      value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })}
                      data-testid="footer-contact-message"
                    />
                  </div>
                  {err && <div className="text-sm text-red-600 mt-2" data-testid="footer-contact-error">{err}</div>}
                  <button
                    type="submit"
                    className="btn-primary mt-4 justify-center w-full sm:w-auto"
                    disabled={busy}
                    data-testid="footer-contact-submit"
                  >
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    {lang === "ar" ? "إرسال رسالة سريعة" : "Send quick message"}
                  </button>
                </>
              )}
            </form>
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 py-16">
        <div className="grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <Wordmark variant="mark" size="lg" />
            <p className="text-sm text-[#525252] leading-relaxed mt-5 max-w-sm">{t("brand.tagline")}</p>
            <div className="mt-5">
              <Vision2030Badge variant="compact" />
            </div>
            <p className="text-xs text-[#8a8a8a] leading-relaxed mt-5 max-w-md">{t("common.legal")}</p>

            {/* App download badges */}
            <div className="mt-6">
              <div className="overline mb-3">{lang === "ar" ? "حمّل التطبيق" : "Get the app"}</div>
              <div className="flex flex-wrap gap-2.5" data-testid="app-store-badges">
                <div
                  className="relative inline-flex items-center gap-2 bg-[#0a0a0a]/90 rounded-xl px-4 py-2.5 cursor-not-allowed opacity-90 select-none"
                  style={{ color: "#ffffff" }}
                  data-testid="app-store-ios"
                  aria-label="App Store — coming soon"
                  title={lang === "ar" ? "قريباً" : "Coming soon"}
                >
                  <svg viewBox="0 0 24 24" className="w-7 h-7 flex-shrink-0" fill="#ffffff"><path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/></svg>
                  <div className="text-start leading-tight" style={{ color: "#ffffff" }}>
                    <div className="text-[10px] opacity-80" style={{ color: "#ffffff" }}>{lang === "ar" ? "حمّل من" : "Download on the"}</div>
                    <div className="text-sm font-semibold" style={{ color: "#ffffff" }}>App Store</div>
                  </div>
                </div>
                <div
                  className="relative inline-flex items-center gap-2 bg-[#0a0a0a]/90 rounded-xl px-4 py-2.5 cursor-not-allowed opacity-90 select-none"
                  style={{ color: "#ffffff" }}
                  data-testid="app-store-android"
                  aria-label="Google Play — coming soon"
                  title={lang === "ar" ? "قريباً" : "Coming soon"}
                >
                  <svg viewBox="0 0 24 24" className="w-7 h-7 flex-shrink-0"><path fill="#34A853" d="M13.07 11.99l-9.43 9.5c.27.1.57.16.88.16.39 0 .77-.1 1.1-.29l8.78-5.06-1.33-4.31z"/><path fill="#FBBC04" d="M21.42 10.51l-3.79-2.18-2.35 2.04 2.93 2.92 3.21-1.84c.96-.55.96-1.95 0-2.5l-.01-.44z"/><path fill="#4285F4" d="M3.64 2.51c-.5.31-.84.86-.84 1.48v16.02c0 .29.07.57.18.81L12.83 11.16 3.64 2.51z"/><path fill="#EA4335" d="M13.13 11.99l4.4-4.4-8.74-5.05c-.32-.19-.7-.29-1.08-.29-.36 0-.7.09-1 .26l6.42 9.48z"/></svg>
                  <div className="text-start leading-tight" style={{ color: "#ffffff" }}>
                    <div className="text-[10px] opacity-80" style={{ color: "#ffffff" }}>{lang === "ar" ? "احصل عليه من" : "Get it on"}</div>
                    <div className="text-sm font-semibold" style={{ color: "#ffffff" }}>Google Play</div>
                  </div>
                </div>
              </div>
              <div className="mt-3 inline-flex items-center gap-2 rounded-full px-3.5 py-1.5" style={{ background: "linear-gradient(135deg,#c9a55c,#8a6f2e)" }}>
                <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
                <span className="text-[13px] font-bold text-[#0a0a0a] tracking-wide" data-testid="app-coming-soon">
                  {lang === "ar" ? "قريباً جداً — سجّل ليصلك إشعار الإطلاق" : "Coming very soon — sign up for launch alert"}
                </span>
              </div>
            </div>
          </div>

          <div className="md:col-span-2">
            <div className="overline mb-4">{t("nav.marketplace")}</div>
            <ul className="space-y-2 text-sm text-[#525252]">
              <li><Link to="/marketplace?type=new" className="hover:text-[#0a0a0a]">{lang === "ar" ? "جديدة" : "New"}</Link></li>
              <li><Link to="/marketplace?type=used" className="hover:text-[#0a0a0a]">{lang === "ar" ? "مستعملة" : "Used"}</Link></li>
              <li><Link to="/rentals" className="hover:text-[#0a0a0a]">{t("nav.rentals")}</Link></li>
              {FEATURES.AUCTIONS_ENABLED && (
                <li><Link to="/auctions" className="hover:text-[#0a0a0a]">{t("nav.auctions")}</Link></li>
              )}
              <li><Link to="/salvage" className="hover:text-[#0a0a0a]">{t("nav.salvage")}</Link></li>
              <li><Link to="/export" className="hover:text-[#0a0a0a]">{t("nav.export")}</Link></li>
            </ul>
          </div>

          <div className="md:col-span-2">
            <div className="overline mb-4">{lang === "ar" ? "خدمات" : "Services"}</div>
            <ul className="space-y-2 text-sm text-[#525252]">
              <li><Link to="/financing" className="hover:text-[#0a0a0a]">{t("nav.financing")}</Link></li>
              <li><Link to="/insurance" className="hover:text-[#0a0a0a]">{t("nav.insurance")}</Link></li>
              <li><Link to="/pre-purchase-inspection" className="hover:text-[#0a0a0a]">{t("nav.prePurchaseInspection")}</Link></li>
              <li><Link to="/periodic-inspection" className="hover:text-[#0a0a0a]">{t("nav.periodicInspection")}</Link></li>
              <li><Link to="/vehicle-history" className="hover:text-[#0a0a0a]">{t("nav.vehicleHistory")}</Link></li>
              <li><Link to="/ownership-transfer" className="hover:text-[#0a0a0a]">{t("nav.ownershipTransfer")}</Link></li>
              <li><Link to="/showrooms" className="hover:text-[#0a0a0a]">{t("nav.showrooms")}</Link></li>
              <li><Link to="/companies" className="hover:text-[#0a0a0a]" data-testid="footer-companies-link">{lang === "ar" ? "دليل الشركات" : "Companies"}</Link></li>
              <li><Link to="/delivery" className="hover:text-[#0a0a0a]">{t("nav.delivery")}</Link></li>
            </ul>
          </div>

          <div className="md:col-span-3">
            <div className="overline mb-4">{lang === "ar" ? "قانوني" : "Legal"}</div>
            <ul className="space-y-2 text-sm text-[#525252]">
              <li><Link to="/trust" className="hover:text-[#0a0a0a]">{t("nav.trust")}</Link></li>
              <li><Link to="/disputes" className="hover:text-[#0a0a0a]">{t("nav.dispute")}</Link></li>
              <li><Link to="/pricing" className="hover:text-[#0a0a0a]">{t("nav.pricing")}</Link></li>
              <li><Link to="/terms" className="hover:text-[#0a0a0a]">{t("footer.terms")}</Link></li>
              <li><Link to="/privacy" className="hover:text-[#0a0a0a]">{t("footer.privacy")}</Link></li>
              <li><Link to="/disclaimer" className="hover:text-[#0a0a0a]">{t("footer.disclaimer")}</Link></li>
              <li><Link to="/refund-policy" className="hover:text-[#0a0a0a]">{t("footer.refund")}</Link></li>
              <li><Link to="/fraud-policy" className="hover:text-[#0a0a0a]">{t("footer.fraud")}</Link></li>
              <li><Link to="/contact" className="hover:text-[#0a0a0a]">{t("nav.contact")}</Link></li>
              <li><Link to="/faq" className="hover:text-[#0a0a0a]" data-testid="footer-faq-link">{lang === "ar" ? "الأسئلة الشائعة" : "FAQ / Help Center"}</Link></li>
            </ul>
          </div>
        </div>
      </div>
      <div className="border-t border-[#0a0a0a]/[0.06]">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 py-6 text-xs text-[#8a8a8a] flex flex-wrap items-center justify-between gap-2">
          <span>{t("footer.copyright")}</span>
          <span>{lang === "ar" ? "صُنع بشغف في ينبع الصناعية" : "Crafted with passion in Yanbu Industrial"}</span>
        </div>
      </div>
    </footer>
  );
}
